import { Search } from "@mui/icons-material";
import { Dispatch, SetStateAction, useState, ChangeEvent } from "react";

interface TableFilterProps {
	filterList: string[];
	setFilteredAds: Dispatch<SetStateAction<IAdvertisement[]>>;
	data: IAdvertisement[];
	type: string;
	totalC: number;
}

const TableFilter = ({
	filterList,
	data,
	setFilteredAds,
	type,
	totalC,
}: TableFilterProps) => {
	const [activeIndex, setActiveIndex] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const tableAds = data.filter((ad) => ad.adType === type);

	// Calculate counts for each status
	const getStatusCounts = () => {
		const counts: { [key: string]: number } = {
			all: totalC,
		};

		// Count ads for each status (e.g., "active", "inactive")
		tableAds.forEach((ad) => {
			const status = ad.status.toLowerCase();
			counts[status] = (counts[status] || 0) + 1;
		});

		return counts;
	};

	const statusCounts = getStatusCounts();

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		const term = e.target.value.toLowerCase();
		setSearchTerm(term);

		if (term === "") {
			handleStatusFilter(filterList[activeIndex]);
		} else {
			const filtered = tableAds.filter((item) =>
				Object.values(item).some((value) =>
					value?.toString().toLowerCase().includes(term)
				)
			);
			setFilteredAds(filtered);
		}
	};

	// Handle status filter change
	const handleStatusFilter = (selectedFilter: string) => {
		const normalizedFilter = selectedFilter.toLowerCase();

		if (normalizedFilter === "all") {
			setFilteredAds(tableAds);
		} else {
			const filtered = tableAds.filter(
				(item) => item.status.toLowerCase() === normalizedFilter
			);
			setFilteredAds(filtered);
		}
	};

	const handleButtonClick = (index: number) => {
		setActiveIndex(index);
		const selectedFilter = filterList[index];
		handleStatusFilter(selectedFilter);

		if (searchTerm) {
			const statusFiltered = tableAds.filter(
				(item) =>
					selectedFilter.toLowerCase() === "all" ||
					item.status.toLowerCase() === selectedFilter.toLowerCase()
			);
			const filtered = statusFiltered.filter((item) =>
				Object.values(item).some((value) =>
					value?.toString().toLowerCase().includes(searchTerm)
				)
			);
			setFilteredAds(filtered);
		}
	};

	const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
		const index = filterList.indexOf(e.target.value);
		setActiveIndex(index);
		handleStatusFilter(e.target.value);

		if (searchTerm) {
			const statusFiltered = tableAds.filter(
				(item) =>
					e.target.value.toLowerCase() === "all" ||
					item.status.toLowerCase() === e.target.value.toLowerCase()
			);
			const filtered = statusFiltered.filter((item) =>
				Object.values(item).some((value) =>
					value?.toString().toLowerCase().includes(searchTerm)
				)
			);
			setFilteredAds(filtered);
		}
	};

	return (
		<div className='mt-6 flex flex-col xl:flex-row gap-4 justify-between'>
			<div className='hidden lg:flex gap-4 overflow-hidden'>
				{filterList.map((item, index) => {
					const count = statusCounts[item.toLowerCase()] || 0;
					return (
						<button
							className={`${
								activeIndex === index
									? "border-b-gray-600"
									: "border-b-transparent"
							} border-b-2 px-2`}
							onClick={() => handleButtonClick(index)}
							key={index}
						>
							{`${item} (${count})`}
						</button>
					);
				})}
			</div>
			<select
				name='statusFilter'
				id='statusFilter'
				className='p-3 rounded-lg border-[1px] border-gray-500 lg:hidden'
				value={filterList[activeIndex]}
				onChange={handleSelectChange}
			>
				{filterList.map((item, index) => {
					const count = statusCounts[item.toLowerCase()] || 0;
					return (
						<option key={index} value={item}>
							{`${item} (${count})`}
						</option>
					);
				})}
			</select>
			<div className='relative'>
				<input
					type='text'
					name='search'
					id='search'
					className='w-full xl:min-w-[439px] border-b-2 text-right pr-12 p-3 rounded-lg'
					placeholder='Search'
					value={searchTerm}
					onChange={handleSearchChange}
				/>
				<i className='absolute top-0 right-0 h-full flex items-center justify-center px-2'>
					<Search className='text-gray-400' />
				</i>
			</div>
		</div>
	);
};

export default TableFilter;
